import React, { useMemo, useState } from 'react';
import {
  Clock, User, AtSign, Mail, X, ChevronDown, ChevronRight,
  Briefcase, CalendarDays, Flag, Tag, ShieldCheck, Hash,
  MessageSquare, CheckCircle, FileText, Lock, UserPlus,
} from 'lucide-react';
import type { Ticket, TicketComment, TicketPriority, TicketStatus } from '../../types';
import { ParticipantAvatar } from '../shared/TicketChatComponents';
import SlaBadge from './tickets/SlaBadge';
import { getTicketSla } from './tickets/ticketSla';
import AssigneeStack, { ticketAssigneesOf } from './tickets/AssigneeStack';
import { auth } from '../../lib/firebase';

export const STATUS_LABELS: Record<TicketStatus, string> = {
  open: 'Open', in_progress: 'In Progress', pending: 'Pending', resolved: 'Resolved', closed: 'Closed',
};

export const STATUS_CONFIG: Record<TicketStatus, { dot: string; text: string; bg: string }> = {
  open: { dot: 'bg-[var(--color-info)]', text: 'text-[var(--color-info)]', bg: 'bg-[var(--color-info-bg)]' },
  in_progress: { dot: 'bg-[var(--color-warning)]', text: 'text-[var(--color-warning)]', bg: 'bg-[var(--color-warning-bg)]' },
  pending: { dot: 'bg-[var(--gray-400)]', text: 'text-[var(--gray-600)]', bg: 'bg-[var(--gray-50)]' },
  resolved: { dot: 'bg-[var(--color-success)]', text: 'text-[var(--color-success)]', bg: 'bg-[var(--color-success-bg)]' },
  closed: { dot: 'bg-[var(--gray-400)]', text: 'text-[var(--gray-400)]', bg: 'bg-[var(--gray-50)]' },
};

export const STATUS_BANNER: Record<TicketStatus, { bar: string }> = {
  open: { bar: 'bg-[var(--color-info)]' },
  in_progress: { bar: 'bg-[var(--color-warning)]' },
  pending: { bar: 'bg-[var(--gray-400)]' },
  resolved: { bar: 'bg-[var(--color-success)]' },
  closed: { bar: 'bg-[var(--gray-400)]' },
};

export function StatusPill({ status }: { status: TicketStatus }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.open;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-[6px] text-[11px] font-semibold ${cfg.bg} ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {STATUS_LABELS[status]}
    </span>
  );
}

export const PRIORITY_CONFIG: Record<TicketPriority, { dot: string; text: string; bg: string }> = {
  urgent: { dot: 'bg-[#C13F3F]', text: 'text-[#C13F3F]', bg: 'bg-[#FBEAEA]' },
  high: { dot: 'bg-[#B7791F]', text: 'text-[#B7791F]', bg: 'bg-[#FDF3E3]' },
  medium: { dot: 'bg-[#2F5FE0]', text: 'text-[#2F5FE0]', bg: 'bg-[#EEF2FE]' },
  low: { dot: 'bg-[#5B6270]', text: 'text-[#5B6270]', bg: 'bg-[#F5F6F8]' },
};

export function PriorityPill({ priority }: { priority: TicketPriority }) {
  const cfg = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.medium;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-[6px] text-[11px] font-semibold ${cfg.bg} ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {priority.charAt(0).toUpperCase() + priority.slice(1)}
    </span>
  );
}

function CollapsibleCard({
  title, icon, defaultOpen, children,
}: {
  title: string;
  icon: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen ?? true);
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex w-full items-center justify-between gap-2 bg-slate-50/80 px-3 py-2 transition-colors hover:bg-slate-100 cursor-pointer"
        aria-expanded={open}
      >
        <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-600">
          {icon}
          {title}
        </span>
        {open ? <ChevronDown className="h-3.5 w-3.5 text-slate-400" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-400" />}
      </button>
      {open && <div className="px-3 py-2.5">{children}</div>}
    </div>
  );
}

interface TicketSplitViewProps {
  ticket: Ticket;
  comments: TicketComment[];
  allAdmins?: { uid: string; name: string; email: string }[];
  onClose: () => void;
  onUpdate: (ticketId: string, updates: Partial<Ticket>) => void;
  onViewApplicant?: (applicantId: string) => void;
  onDeleted?: () => void;
  /** Applicant profile photo URL (from the applications list). */
  applicantPhotoUrl?: string;
}

/**
 * Right-hand details/overview sidebar for the ticket split layout.
 * Renders ONLY metadata (Status, Priority, Assigned To, Applicant info,
 * Timestamps) — the conversation is rendered by TicketsPage via the
 * embedded TicketDetailModal. Keeping the chat out of this component
 * prevents the duplicate detail screens / stray full-screen modal.
 */
export default function TicketSplitView({ ticket, comments, allAdmins, onClose, onUpdate, onViewApplicant, onDeleted, applicantPhotoUrl }: TicketSplitViewProps) {
  const banner = STATUS_BANNER[ticket.status];
  // One-click self-assignment: "Assigned To: —" on every row was the most
  // common complaint, and picking your own name out of a dropdown is two clicks
  // more than it needs to be.
  const currentUserId = auth.currentUser?.uid || '';
  const currentUserName = auth.currentUser?.displayName || auth.currentUser?.email || 'Me';
  // 0..N assignees. Reads the legacy single-owner fields as a one-person list,
  // so tickets written before multi-assignee existed still show their owner.
  const assignees = ticketAssigneesOf(ticket);
  const timeOpen = ticket.createdAt
    ? Math.max(0, Math.floor((Date.now() - new Date(ticket.createdAt).getTime()) / 86400000))
    : 0;
  const activeComments = (comments || []).filter(c => !isAuditComment(c) && !c.isInternal);

  // Chronological lifecycle timeline (most recent first, capped at 10 events)
  const timeline = useMemo(() => {
    const events: { time: string; label: string; type: string; by?: string }[] = [];
    if (ticket.createdAt) events.push({ time: ticket.createdAt, label: 'Ticket created', type: 'system', by: ticket.applicantName || 'Applicant' });
    if (ticket.resolvedAt) events.push({ time: ticket.resolvedAt, label: 'Ticket resolved', type: 'success' });
    if (ticket.closedAt) events.push({ time: ticket.closedAt, label: 'Ticket closed', type: 'neutral' });
    (comments || []).forEach((c) => {
      if (isAuditComment(c)) {
        events.push({ time: c.createdAt, label: c.body.replace(/^\[[^\]]*\]\s*/, ''), type: 'audit', by: c.authorName });
      } else if (c.isInternal) {
        events.push({ time: c.createdAt, label: 'Internal note added', type: 'internal', by: c.authorName });
      } else {
        events.push({
          time: c.createdAt,
          label: c.authorRole === 'applicant' ? 'Applicant replied' : 'Agent replied',
          type: c.authorRole === 'applicant' ? 'applicant' : 'agent',
          by: c.authorName,
        });
      }
    });
    events.sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());
    return events.slice(-10).reverse();
  }, [comments, ticket]);

  return (
    <aside className="flex h-full w-full min-w-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="shrink-0 border-b border-slate-200 bg-gradient-to-r from-slate-900 via-slate-800 to-blue-900 px-3.5 py-2.5 text-white">
        <div className="flex items-center justify-between gap-2.5">
          <div className="min-w-0">
            <h4 className="text-[11px] font-bold uppercase tracking-wide text-white/85">Ticket Details</h4>
            <p className="mt-0.5 truncate font-mono text-[11px] text-slate-300">#{ticket.ticketId.slice(0, 12)} · {ticket.programName || 'No program'}</p>
          </div>
          {/* Always available: as a grid column this COLLAPSES the panel; in the
              drawer it closes it. It used to be `lg:hidden`, so an inline panel
              could only be dismissed by widening the window. */}
          <button
            onClick={onClose}
            className="shrink-0 rounded-lg border border-white/10 bg-white/5 p-1.5 transition-colors hover:bg-white/15 cursor-pointer"
            type="button"
            title="Collapse details"
            aria-label="Collapse ticket details"
          >
            <X className="h-4 w-4 text-slate-200" />
          </button>
        </div>
      </div>

      <div className={`shrink-0 h-1 ${banner.bar}`} aria-hidden="true" />

      <div className="flex-1 overflow-y-auto bg-slate-50/60 p-2.5 space-y-2.5">
        <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-sm">
          {(() => {
            const src = ticket.source || (ticket.employerId ? 'employer' : ticket.referrerId ? 'referrer' : ticket.sponsorId ? 'sponsor' : ticket.createdByRole);
            if (src === 'employer') return <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-extrabold">Employer Ticket</span>;
            if (src === 'referrer') return <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 text-[10px] font-extrabold">Referrer Ticket</span>;
            if (src === 'sponsor') return <span className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 text-[10px] font-extrabold">Sponsor Ticket</span>;
            return null;
          })()}
          <StatusPill status={ticket.status} />
          <span
            className="ml-auto text-[10px] font-semibold text-slate-500"
            title={`This ticket was opened ${timeOpen} day${timeOpen === 1 ? '' : 's'} ago (elapsed calendar time).`}
          >
            Open for {timeOpen} day{timeOpen === 1 ? '' : 's'}
          </span>
        </div>

        <CollapsibleCard title="Applicant" icon={<User className="h-3 w-3" />}>
          <div className="flex items-center gap-3 mb-2.5">
            <ParticipantAvatar
              name={ticket.applicantName}
              photoUrl={applicantPhotoUrl}
              size="lg"
            />
            <div className="min-w-0">
              <p className="text-xs font-bold text-slate-800 truncate">{ticket.applicantName || '—'}</p>
              <p className="text-[11px] text-slate-500 truncate">{ticket.applicantEmail || ''}</p>
            </div>
          </div>
          <div className="space-y-1.5 text-xs">
            {ticket.programName && (
              <p className="text-slate-500 flex items-center gap-1.5">
                <Briefcase className="h-3.5 w-3.5 text-slate-300 shrink-0" />
                <span className="truncate">{ticket.programName}</span>
              </p>
            )}
            {ticket.applicationId && (
              <p className="text-[11px] text-slate-500 font-mono flex items-center gap-1.5">
                <AtSign className="h-3 w-3 text-slate-300 shrink-0" /> App: {ticket.applicationId}
              </p>
            )}
          </div>
          {ticket.applicationId && onViewApplicant && (
            <button onClick={() => onViewApplicant(ticket.applicationId!)}
              className="mt-3 w-full px-3 py-2 bg-blue-50 hover:bg-blue-100 text-brand text-xs font-bold rounded-xl transition-all cursor-pointer min-h-[36px]">
              View Profile
            </button>
          )}
        </CollapsibleCard>

        {ticket.description && (
          <CollapsibleCard title="Description" icon={<FileText className="h-3 w-3" />} defaultOpen={true}>
            <p className="text-xs text-slate-600 whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto pr-1">
              {ticket.description}
            </p>
          </CollapsibleCard>
        )}

        <CollapsibleCard title="Details" icon={<Tag className="h-3 w-3" />} defaultOpen={true}>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1.5">Status</label>
              <div className="flex items-center gap-2">
                <select value={ticket.status} onChange={(e) => onUpdate(ticket.ticketId, { status: e.target.value as TicketStatus })}
                  className="flex-1 px-3 py-2 text-xs border border-slate-200 rounded-xl bg-white font-bold cursor-pointer">
                  {(Object.entries(STATUS_LABELS) as [TicketStatus, string][]).map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
                {ticket.status !== 'closed' ? (
                  <button
                    onClick={() => onUpdate(ticket.ticketId, { status: 'closed' })}
                    className="px-3 py-2 bg-rose-50 hover:bg-rose-100 text-rose-600 border border-rose-200 text-xs font-bold rounded-xl transition-all cursor-pointer whitespace-nowrap flex items-center gap-1"
                    title="Close Ticket"
                    type="button"
                  >
                    <Lock className="h-3.5 w-3.5" /> Close
                  </button>
                ) : (
                  <button
                    onClick={() => onUpdate(ticket.ticketId, { status: 'open' })}
                    className="px-3 py-2 bg-blue-50 hover:bg-blue-100 text-brand border border-blue-200 text-xs font-bold rounded-xl transition-all cursor-pointer whitespace-nowrap"
                    title="Reopen Ticket"
                    type="button"
                  >
                    Reopen
                  </button>
                )}
              </div>
            </div>
            <div>
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1.5">Priority</label>
              <select value={ticket.priority} onChange={(e) => onUpdate(ticket.ticketId, { priority: e.target.value as TicketPriority })}
                className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl bg-white font-bold cursor-pointer">
                {(['urgent', 'high', 'medium', 'low'] as TicketPriority[]).map(p => (
                  <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                ))}
              </select>
            </div>
            {!!getTicketSla(ticket).label && (
              <div>
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1.5">Response clock</label>
                <SlaBadge ticket={ticket} showResolved />
              </div>
            )}
            <div>
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1.5">
                Assigned To
              </label>
              {/* MULTI-ASSIGNEE — 0..N teammates. The whole array is sent; the
                  server derives the legacy single owner from the first entry, so
                  the ticket list, filters and assignment email keep working. */}
              <AssigneeStack
                assignees={assignees}
                agents={(allAdmins || []).map(a => ({ uid: a.uid, name: a.name, email: a.email }))}
                onChange={(next) => onUpdate(ticket.ticketId, {
                  assignees: next,
                  assignedTo: next[0]?.uid ?? null,
                  assignedToName: next[0]?.name ?? null,
                })}
              />
              {!!currentUserId && !assignees.some(a => a.uid === currentUserId) && (
                <button
                  type="button"
                  onClick={() => onUpdate(ticket.ticketId, {
                    assignees: [
                      { uid: currentUserId, name: currentUserName, role: 'owner' as const, assignedAt: new Date().toISOString() },
                      ...assignees,
                    ],
                    assignedTo: currentUserId,
                    assignedToName: currentUserName,
                  })}
                  className="mt-1.5 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-blue-200 bg-brand-active px-3 py-1.5 text-[11px] font-bold text-brand transition-all hover:bg-blue-100 cursor-pointer"
                  title="Assign this ticket to me as the owner"
                >
                  <UserPlus className="h-3 w-3" aria-hidden="true" />
                  Assign to me
                </button>
              )}
              <p className="mt-1 text-[10px] font-semibold text-slate-400">
                {assignees.length === 0
                  ? 'Nobody is assigned yet.'
                  : `${assignees.length} teammate${assignees.length === 1 ? '' : 's'} on this ticket`}
              </p>
            </div>
          </div>
        </CollapsibleCard>

        <CollapsibleCard title="Conversation" icon={<Hash className="h-3 w-3" />} defaultOpen={false}>
          <div className="space-y-1.5 text-[11px] text-slate-500">
            <p className="flex items-center gap-1.5"><MessageSquare className="h-3 w-3 text-slate-300 shrink-0" /> {activeComments.length} message(s) in this ticket</p>
            <p className="flex items-center gap-1.5"><ShieldCheck className="h-3 w-3 text-slate-300 shrink-0" /> {(comments || []).filter(c => c.isInternal).length} internal note(s)</p>
            <p className="flex items-center gap-1.5"><Flag className="h-3 w-3 text-slate-300 shrink-0" /> Priority: {ticket.priority}</p>
          </div>
        </CollapsibleCard>

        <CollapsibleCard title="Timeline" icon={<CalendarDays className="h-3 w-3" />} defaultOpen={false}>
          {timeline.length === 0 ? (
            <p className="text-[11px] text-slate-500">No activity yet.</p>
          ) : (
            <div className="space-y-0">
              {timeline.map((ev, i) => (
                <div key={`${ev.time}-${i}`} className="flex gap-2">
                  <div className="flex flex-col items-center shrink-0">
                    <span className={`w-2 h-2 rounded-full mt-1 ${dotColor(ev.type)}`} />
                    {i < timeline.length - 1 && <span className="w-px flex-1 bg-slate-100 my-0.5" />}
                  </div>
                  <div className="pb-3 min-w-0">
                    <p className="text-[10px] font-semibold text-slate-700 leading-snug">{ev.label}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      {ev.by ? `${ev.by} · ` : ''}{new Date(ev.time).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CollapsibleCard>
      </div>
    </aside>
  );
}

function formatActivity(iso: string): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
  return new Date(iso).toLocaleDateString();
}

function dotColor(type: string): string {
  switch (type) {
    case 'applicant': return 'bg-blue-500';
    case 'agent': return 'bg-emerald-500';
    case 'success': return 'bg-emerald-500';
    case 'neutral': return 'bg-slate-400';
    case 'internal': return 'bg-amber-400';
    case 'audit': return 'bg-purple-400';
    default: return 'bg-slate-300';
  }
}

const AUDIT_PREFIXES = ['Status changed', 'Priority changed', 'Assigned to', 'Title updated', 'Description updated', 'Archived', 'Restored from'];
function isAuditComment(c: TicketComment): boolean {
  return !!c.isInternal && AUDIT_PREFIXES.some(p => (c.body || '').startsWith(p));
}

function getInitials(name?: string): string {
  if (!name) return '?';
  return name.trim().split(/\s+/).slice(0, 2).map(p => p.charAt(0).toUpperCase()).join('') || '?';
}
